// 📁 src/components/TableauCompta.jsx
import React, { useState, useMemo } from "react";
import axios from "axios";

import { useMediaQuery, Tooltip, Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useGridApiRef } from "@mui/x-data-grid";

import { useAdminRole } from "../../hooks/useAdminRole.js";

import { useGroupe } from "../../context/GroupeContext.js"; 
import { useLanguage } from "../../context/LanguageContext.js"; 

import { logDev } from "../../utils/logger.js";


const TableauCompta = ({ rows = [], isAdmin = false, onRefresh }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const { t } = useLanguage();

  // ✅ Récupère l’ID du groupe courant
  const { groupeId } = useGroupe();
  const role = useAdminRole(groupeId);
  const [pageSize, setPageSize] = useState(10);
  const apiRef = useGridApiRef();

  logDev("[TableauCompta] rendu", { groupeId, role, isAdmin });
  logDev("[TableauCompta] props reçus", { rowsCount: rows.length });

  // ✅ Prépare les lignes du tableau à partir des contacts
  const baseRows = useMemo(() => {
    const safe = Array.isArray(rows) ? rows : [];
    logDev("[TableauCompta] baseRows calculés", rows);
      return safe;
    }, [rows]);

  // ✅ Ajoute une ligne TOTAL calculée automatiquement
  const rowsWithoutTotal = useMemo(() => {
    return baseRows;
  }, [baseRows]);

  const totalRow = useMemo(() => ({
  virement1: baseRows.reduce((sum, r) => sum + (Number(r.virement1) || 0), 0),
  virement2: baseRows.reduce((sum, r) => sum + (Number(r.virement2) || 0), 0),
  }), [baseRows]);

  const totalGlobal = totalRow.virement1 + totalRow.virement2;

  // ✅ Définition des colonnes du tableau
  const columns = [
    {
      field: "nom",
      headerName: isMobile ? "" : "Contact",
      flex: 1,
      minWidth: 120,
      renderHeader: () =>
        isMobile ? (
          <Tooltip title="Contact" arrow>
            <PersonIcon fontSize="small" />
          </Tooltip>
        ) : (
          t("Contacts")
        )
    },
    {
      field: "virement1",
      headerName: isMobile ? "" : "Virement 1",
      type: "number",
      flex: 1,
      minWidth: 90,
      editable: isAdmin,
      renderHeader: () =>
        isMobile ? (
          <Tooltip title="Virement 1" arrow>
            <AttachMoneyIcon fontSize="small" />
          </Tooltip>
        ) : (
          t("Payment1")
        )
    },
    
{
  field: "date1",
  headerName: isMobile ? "" : "Date 1",
  flex: 1,
  editable: isAdmin,
  minWidth: 120,
  renderHeader: () =>
    isMobile ? (
      <Tooltip title="Date 1" arrow>
        <EventIcon fontSize="small" />
      </Tooltip>
    ) : (
      t("Date1")
    ),
  renderCell: (params) => {
    const val = params.value;
    if (!val) return ""; // 👈 évite "null"
    const date = new Date(val);
    return !isNaN(date)
      ? date.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        })
      : "";
  },
  
  renderEditCell: (params) => (
    <input
      type="date"
      value={params.value ? new Date(params.value).toISOString().split("T")[0] : ""}
      onChange={(e) => {
        params.api.setEditCellValue({
          id: params.id,
          field: "date1",
          value: e.target.value
        });
      }}
      style={{ width: "100%", padding: "6px" }}
    />
  )
},


    {
      field: "virement2",
      headerName: isMobile ? "" : "Virement 2",
      type: "number",
      flex: 1,
      minWidth: 90,
      editable: isAdmin,
      renderHeader: () =>
        isMobile ? (
          <Tooltip title="Virement 2" arrow>
            <AttachMoneyIcon fontSize="small" />
          </Tooltip>
        ) : (
          t("Payment2")
        )
    },
    {
      field: "date2",
      headerName: isMobile ? "" : "Date 2",
      editable: isAdmin,
      flex: 1,
      minWidth: 120,
      renderHeader: () =>
      isMobile ? (
      <Tooltip title="Date 2" arrow>
        <EventIcon fontSize="small" />
      </Tooltip>
    ) : (
      t("Date2")
    ),
  renderCell: (params) => {
    const val = params.value;
    if (!val) return "";
    const date = new Date(val);
    return !isNaN(date)
      ? date.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        })
      : "";
  },

  renderEditCell: (params) => (
    <input
      type="date"
      value={params.value ? new Date(params.value).toISOString().split("T")[0] : ""}
      onChange={(e) => {
        params.api.setEditCellValue({
          id: params.id,
          field: "date2",
          value: e.target.value
        });
      }}
      style={{ width: "100%", padding: "6px" }}
    />
  )
}
  ];

  // ✅ Fonction appelée quand une ligne est modifiée
  const processRowUpdate = async (newRow, oldRow) => {
    if (!isAdmin) return oldRow;    // non-admin → pas de modif
    if (newRow._id === "total") return oldRow;  // ligne TOTAL non éditable
    try {
      logDev("[TableauCompta] tentative update", { oldRow, newRow });
      // 🔄 Mise à jour côté API

      logDev("📡 Envoi PUT vers /compta", {
        contactId: newRow._id,
        groupeId,
        virement1: newRow.virement1,
        date1: newRow.date1,
        virement2: newRow.virement2,
        date2: newRow.date2
      });

      await axios.put(`${process.env.REACT_APP_API}/compta/${newRow._id}`, {
        virement1: newRow.virement1,
        date1: newRow.date1,
        virement2: newRow.virement2,
        date2: newRow.date2,
        groupeId: groupeId
      });

      logDev("[TableauCompta] update réussi", newRow);
      if (onRefresh) onRefresh();
      return newRow; // ✅ pas de setRows ici, c'est contactsDominante qui drive
    } catch (err) {
      logDev("❌ Erreur mise à jour compta", { message: err.message, stack: err.stack });
      logDev("[TableauCompta] update échoué", err);
      return oldRow;
    }
  };

  const handleCellClick = (params, event) => {
  if (!isAdmin) return;
  if (params.row._id === "total") return;

  // Empêche la sélection de ligne
  event.defaultMuiPrevented = true;

  // 🔥 Vérifie si la cellule est déjà en édition
  const mode = apiRef.current.getCellMode(params.id, params.field);
  if (mode === "edit") {
    return; // ← Évite l’erreur MUI X
  }

  // Passe en mode edition
  apiRef.current.startCellEditMode({
    id: params.id,
    field: params.field
  });

  // Si la cellule contient "0", on la vide
  if (params.value === 0 || params.value === "0") {
    setTimeout(() => {
      apiRef.current.setEditCellValue({
      id: params.id,
      field: params.field,
      value: ""
    });

    // 🔥 Sélection automatique du champ
      const input = document.querySelector('input[data-field="' + params.field + '"]');
      if (input) input.select();
    }, 0);
  }




  //const isEditable =
  //  typeof params.colDef.editable === "function"
  //    ? params.colDef.editable(params)
  //    : !!params.colDef.editable;

  //if (!isEditable) return;
  //const cellState = apiRef.current.getCellMode(params.id, params.field);
  //if (cellState === "edit") return; // ✅ déjà en édition → on ne relance pas

  // Empêche le comportement par défaut
  //event.defaultMuiPrevented = true;

  // Lance l’édition immédiatement
  //requestAnimationFrame(() => {
  //  apiRef.current.startCellEditMode({
  //    id: params.id,
  //    field: params.field
  //  });
  //});
};

  logDev("[TableauCompta] contenu des rows", baseRows.map(r => ({
    nom: r.nom,
    date1: r.date1,
    date2: r.date2
  })));

  // ✅ Rendu du tableau
  return (
    <>
    {baseRows.length === 0 && (
      <Box
        sx={{
          textAlign: "center",
          mt: 3,
          mb: 2,
          color: "#777",
          fontStyle: "italic",
          fontSize: "1rem"
        }}
      >
        {t("NoAccountingRecordsFoundForThisGroup")}
      </Box>
    )}

<Box sx={{ width: "100%", overflowX: "auto" }}>
    <DataGrid
      apiRef={apiRef}
      onCellClick={handleCellClick}
      rows={rowsWithoutTotal}
      columns={columns}
      getRowId={(row) => row._id}
      disableRowSelectionOnClick
      autoHeight
      processRowUpdate={processRowUpdate}
      experimentalFeatures={{ newEditingApi: true }}
      pagination
      pageSizeOptions={[5, 10, 25]}
      initialState={{
        pagination: { paginationModel: { pageSize: 10, page: 0 } }
      }}

      /*getRowClassName={(params) =>
        params.id === "total" ? "total-row" : ""
      }*/

     getCellClassName={(params) => { 
      const isValidDate = (d) => { 
      if (!d) return false;
      const date = new Date(d); 
      return !isNaN(date.getTime()); 
    }; 


    const d1 = params.row.date1; 
    const d2 = params.row.date2; 

    if (isValidDate(d1) && isValidDate(d2)) { 
    return "row-ok"; 
  } 

return ""; 
}}

      sx={{
        
        width: "100%",

        "& .MuiDataGrid-row:not(.row-ok):not(.total-row)": { 
        backgroundColor: "#e4dcd2",
         },

        "& .total-row": {
          fontWeight: "bold",
          backgroundColor: "#d9cbb8",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "#e4dcd2",
        },
        "& .MuiDataGrid-columnHeader": {
          backgroundColor: "#e4dcd2",
          color: "#555",
        },
        "& .MuiDataGrid-sortIcon": {
          backgroundColor: "transparent"
        },
        "& .MuiDataGrid-columnSeparator": {
          color: "#999",
          visibility: "visible",
          opacity: 1,
        },
        "& .row-ok": { 
        backgroundColor: "#c8e6c9 !important", 
        color: "#1b5e20", 
      fontWeight: "bold", 
    },
      }}
    />

    <Box
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: "#d9cbb8",
        fontWeight: "bold",
        borderRadius: 2,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "space-between",
        gap: 1,
        width: "100%",
        maxWidth: "100%",
        flexWrap: "wrap"
      }}
    >
   <Box
      sx={{
        mt: 2,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: 2,
        flexWrap: "wrap",
        justifyContent: isMobile ? "center" : "flex-start",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#d9cbb8",
          p: 2,
          borderRadius: 2,
          fontWeight: "bold",
          minWidth: 200
        }}
      >
        {t("Payment1")} : {totalRow.virement1}
      </Box>

      <Box
        sx={{
          backgroundColor: "#d9cbb8",
          p: 2,
          borderRadius: 2,
          fontWeight: "bold",
          minWidth: 200
        }}
      >
        {t("Payment2")} : {totalRow.virement2}
      </Box>

      <Box
        sx={{
          backgroundColor: "#c4b8a4",
          p: 2,
          borderRadius: 2,
          fontWeight: "bold",
          minWidth: 200
        }}
      >
        {t("Total")} : {totalGlobal}
      </Box>
    </Box>
  </Box>
</Box>
</>

  );
};

export default TableauCompta;
